import { useState } from 'react';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DibujoExplicativo } from './dibujo-explicativo';
import { ComoSeCombinan } from './como-se-combinan';
import { LineaDeAyuda } from '@/features/compite/ui/linea-de-ayuda';

const CLAVE = 'prixia:reglas:ayuda-oculta';

function leer(): boolean {
  try {
    return localStorage.getItem(CLAVE) === '1';
  } catch {
    return false;
  }
}
function guardar(v: boolean) {
  try {
    localStorage.setItem(CLAVE, v ? '1' : '0');
  } catch {
    /* sin almacenamiento: se muestra siempre */
  }
}

/**
 * «Cómo funcionan» (maqueta S1): las tres frases del spec 208 §1 y el ejemplo de
 * S0. Pulido (spec 232): un recuadro bajo, en tres columnas, con «Ver un
 * ejemplo» y «No mostrar más» al final de la tercera. Lote B2: la segunda frase
 * dice la jerarquía, y el ejemplo empieza por «Cómo se combinan» (#b2-combinan).
 *
 * `compacta` (Compite › Estrategia, la maqueta): cerrada por omisión, es una sola
 * línea — «ⓘ Las reglas son límites… ¿Cómo funcionan?» — y el recuadro se abre
 * y se cierra desde ahí. No recuerda nada: cada visita arranca en la línea.
 */
export function AyudaReglas({ compacta = false }: { compacta?: boolean } = {}) {
  const [oculta, setOculta] = useState(leer);
  const [abierta, setAbierta] = useState(false);
  const [conEjemplo, setConEjemplo] = useState(false);

  if (compacta) {
    return (
      <div className="space-y-3">
        <LineaDeAyuda
          accion={
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 font-semibold"
              aria-expanded={abierta}
              onClick={() => setAbierta((v) => !v)}
            >
              {abierta ? 'Ocultar' : '¿Cómo funcionan?'}
            </Button>
          }
        >
          Las <strong className="font-semibold text-foreground">reglas</strong> son límites que
          ningún precio puede pasar. Las estrategias calculan precios dentro de esos límites.
        </LineaDeAyuda>
        {abierta && recuadro(null)}
      </div>
    );
  }

  if (oculta) {
    return (
      <Button
        variant="link"
        className="h-auto gap-1.5 px-0 font-semibold"
        onClick={() => {
          setOculta(false);
          guardar(false);
        }}
      >
        <Info className="h-4 w-4" aria-hidden="true" />
        ¿Cómo funcionan las reglas?
      </Button>
    );
  }
  return recuadro(() => {
    setOculta(true);
    guardar(true);
  });

  /**
   * Compite (rediseño del 14-oct): las tres frases, en tres columnas (una debajo
   * de la otra en una pantalla angosta). `alNoMostrarMas` null: sin «No mostrar
   * más» (la versión compacta se cierra desde su línea).
   */
  function recuadro(alNoMostrarMas: (() => void) | null) {
    return (
      <section
        aria-label="Cómo funcionan"
        className="rounded-tarjeta border border-dashed border-border bg-card px-5 py-4"
      >
        <div className="grid gap-4 text-sm leading-relaxed text-foreground/80 md:grid-cols-3">
          <p>
            <strong className="font-semibold text-foreground">Una regla es un límite</strong> (en %,
            o en Bs para un solo producto) y a quién alcanza: todo el catálogo, categorías, marcas o
            las dos, con excepciones.
          </p>
          <p>
            <strong className="font-semibold text-foreground">
              Si varias alcanzan al mismo producto,
            </strong>{' '}
            del mismo tipo rige la más específica (este producto, su marca o categoría, todo el
            catálogo); de tipos distintos se cumplen todas y decide la más exigente (del redondeo
            corre una sola).
          </p>
          <p>
            <strong className="font-semibold text-foreground">
              Si no hay precio que las cumpla,
            </strong>{' '}
            es un conflicto entre reglas: el producto no se mueve y te espera en Por decidir.{' '}
            <Button
              variant="link"
              size="sm"
              className="h-auto px-0"
              onClick={() => setConEjemplo((v) => !v)}
            >
              {conEjemplo ? 'Ocultar el ejemplo' : 'Ver un ejemplo'}
            </Button>
            {alNoMostrarMas && (
              <>
                <span className="text-muted-foreground"> · </span>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto px-0 text-muted-foreground"
                  onClick={alNoMostrarMas}
                >
                  No mostrar más
                </Button>
              </>
            )}
          </p>
        </div>
        {conEjemplo && (
          <div className="mt-3 space-y-3">
            <ComoSeCombinan />
            <p className="text-xs text-muted-foreground">
              La estrategia pide un precio. Cada regla pone un límite; en verde, donde se cumplen
              todas. Queda el precio más cercano adentro del verde, y el redondeo lo termina.
            </p>
            <DibujoExplicativo />
          </div>
        )}
      </section>
    );
  }
}
